import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/context/LanguageContext';

interface HeroSearchBoxProps {
  className?: string;
}

const HeroSearchBox: React.FC<HeroSearchBoxProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [sessionFormat, setSessionFormat] = useState('any');
  const [detectingLocation, setDetectingLocation] = useState(false);

  // Auto-detect location on mount
  useEffect(() => {
    if (navigator.geolocation && !location) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Simulated reverse geocoding
          setLocation('New York, USA');
        },
        (error) => {
          console.log('Geolocation not available');
        }
      );
    }
  }, []);

  const detectUserLocation = () => {
    setDetectingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setTimeout(() => {
            setLocation('New York, USA');
            setDetectingLocation(false);
            toast({
              title: "Location detected",
              description: "New York, USA"
            });
          }, 1000);
        },
        (error) => {
          setDetectingLocation(false);
          toast({
            title: "Could not detect location",
            description: "Please enter your location manually",
            variant: "destructive"
          });
        }
      );
    }
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams();
    
    if (query) searchParams.set('q', query);
    if (location) searchParams.set('location', location);
    if (sessionFormat === 'online') searchParams.set('online', 'true');
    if (sessionFormat === 'inperson') searchParams.set('inperson', 'true');
    
    navigate(`/find?${searchParams.toString()}`);
  };

  const popularSearches = [
    { label: 'Anxiety', query: 'anxiety' },
    { label: 'Depression', query: 'depression' },
    { label: 'Relationships', query: 'relationships' },
    { label: 'Trauma', query: 'trauma' }
  ];

  return (
    <Card className={`max-w-4xl mx-auto shadow-2xl border-2 border-primary/20 ${className}`}>
      <CardContent className="p-6 md:p-8">
        {/* Session Format Tabs */}
        <Tabs value={sessionFormat} onValueChange={setSessionFormat} className="mb-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="online" className="text-base">
              🌐 Online
            </TabsTrigger>
            <TabsTrigger value="any" className="text-base">
              ✨ Any
            </TabsTrigger>
            <TabsTrigger value="inperson" className="text-base">
              📍 In-Person
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* Search Fields */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input 
              placeholder="What brings you to therapy?"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-14 pl-12 text-lg"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input 
              placeholder="Your location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="h-14 pl-12 pr-24 text-lg"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute right-2 top-1/2 -translate-y-1/2 h-10"
              onClick={detectUserLocation}
              disabled={detectingLocation}
            >
              {detectingLocation ? 'Detecting...' : 'Detect'}
            </Button>
          </div>
        </div>
        
        {/* Main CTA */}
        <Button 
          size="lg" 
          className="w-full h-14 text-lg font-semibold"
          onClick={handleSearch}
        >
          Find My Therapist →
        </Button>
        
        {/* Popular Searches */}
        <div className="flex flex-wrap gap-2 mt-6 justify-center">
          <span className="text-sm text-muted-foreground mr-2">Popular:</span>
          {popularSearches.map((search) => (
            <Badge 
              key={search.query}
              variant="outline" 
              className="cursor-pointer hover:bg-accent"
              onClick={() => {
                const searchParams = new URLSearchParams();
                searchParams.set('q', search.query);
                if (location) searchParams.set('location', location);
                if (sessionFormat === 'online') searchParams.set('online', 'true');
                if (sessionFormat === 'inperson') searchParams.set('inperson', 'true');
                navigate(`/find?${searchParams.toString()}`);
              }}
            >
              {search.label}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default HeroSearchBox;
